import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Loader } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  getGetApiCompaniesCurrentServicemodulesTextsServiceModuleIdQueryKey,
  useGetApiCompaniesCurrentServicemodulesTextsServiceModuleId,
  usePatchApiCompaniesCurrentServicemodulesTextsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleModel, TextServiceModuleModel } from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { Label } from '@/components/shadcn/label'
import { Textarea } from '@/components/shadcn/textarea'
import { notify } from '@/lib/notify'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

const TRANSLATABLE_PROPERTIES = ['text']

/**
 * Editor for Text modules: standard fields (column 1) + the text content (column 2).
 * Saves everything in a single request via the Text endpoint (full update model).
 */
export function EditTextServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-text', 'service-modules'])
  const std = useStandardServiceModuleFields(module)
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetApiCompaniesCurrentServicemodulesTextsServiceModuleId(module.id)
  const patch = usePatchApiCompaniesCurrentServicemodulesTextsServiceModuleId()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ServiceModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })

  const detail = ((data as any)?.data ?? data) as TextServiceModuleModel | undefined

  const [text, setText] = useState('')
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (detail && !loaded) {
      setText(detail.text ?? '')
      setLoaded(true)
    }
  }, [detail, loaded])

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: { ...std.buildBasePayload(), text: text || null },
      })
      await std.saveTranslations()
      await translations.save(String(module.id))
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesTextsServiceModuleIdQueryKey(module.id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
        }),
      ])
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="module-text">{t('editText.textLabel')}</Label>
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-muted-foreground">
          <Loader className="size-4 animate-spin text-primary" />
          <span className="text-sm">{t('editText.loading')}</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <Textarea
            id="module-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('editText.textPlaceholder')}
            className="min-h-64"
          />
          <TranslateButton
            dialogTitle={t('editText.textTranslateTitle')}
            value={translations.get('text')}
            onChange={(v) => translations.set('text', v)}
          />
        </div>
      )}
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editText.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={std} moduleType={module.type} />}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending || !loaded}
    />
  )
}
